import Icon from '@components/Icon/Icon';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import { useStationMode } from '@hooks/station';
import {
  AVAILABLE_LOCALES,
  getAvailableLocalesForTrack,
  getLocaleFromUrl,
  getPreferredLocale,
  setPreferredLocale,
} from '@utils/localeUtils';
import React, { useCallback, useEffect, useRef, useState } from 'react';

interface LanguageSelectorProps {
  className?: string;
  size?: number;
  availableLocales?: string[]; // Locales available for current track
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  className = '',
  size = 20,
  availableLocales,
}) => {
  const {
    preferredLocale,
    setPreferredLocale: setStoreLocale,
    activePlaylist,
  } = useMusicState();
  const isStationMode = useStationMode();
  const [showDropdown, setShowDropdown] = useState(false);
  const [currentLocale, setCurrentLocale] = useState(preferredLocale);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize locale from URL or localStorage on mount
  useEffect(() => {
    const urlLocale = getLocaleFromUrl();
    const storedLocale = getPreferredLocale();
    const initialLocale = urlLocale || storedLocale;

    if (initialLocale !== currentLocale) {
      setCurrentLocale(initialLocale);
      setStoreLocale(initialLocale);
    }
  }, []);

  // Get available locales for current track
  const getFilteredLocales = useCallback(() => {
    if (availableLocales && availableLocales.length > 0) {
      return AVAILABLE_LOCALES.filter((locale) =>
        availableLocales.includes(locale.code),
      );
    }

    // If we have an active track, check its available locales
    if (activePlaylist && activePlaylist.album.tracks.length > 0) {
      const currentTrack =
        activePlaylist.album.tracks[activePlaylist.currentIndex];
      const trackLocales = getAvailableLocalesForTrack(currentTrack);
      if (trackLocales.length > 0) {
        return AVAILABLE_LOCALES.filter((locale) =>
          trackLocales.includes(locale.code),
        );
      }
    }

    // If no locale information available, don't show selector
    // (returning empty array will cause component to not render)
    return [];
  }, [availableLocales, activePlaylist]);

  const handleLocaleChange = useCallback(
    (locale: string) => {
      setCurrentLocale(locale);
      setStoreLocale(locale);
      setPreferredLocale(locale);
      setShowDropdown(false);

      // Update URL with new locale for sharing
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        url.searchParams.set('locale', locale);
        window.history.replaceState({}, '', url.toString());
      }
    },
    [setStoreLocale],
  );

  // Handle mouse enter - show dropdown immediately
  const handleMouseEnter = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setShowDropdown(true);
  }, []);

  // Handle mouse leave - hide dropdown with delay
  const handleMouseLeave = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => {
      setShowDropdown(false);
    }, 150); // Small delay to allow moving to dropdown
  }, []);

  // Handle click for mobile devices
  const handleClick = useCallback(() => {
    setShowDropdown((prev) => !prev);
  }, []);

  // Handle click outside to close dropdown on mobile
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showDropdown && !target.closest('.language-selector-container')) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showDropdown]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const filteredLocales = getFilteredLocales();
  const currentLocaleInfo = AVAILABLE_LOCALES.find(
    (l) => l.code === currentLocale,
  );
  // Don't render if only one locale is available
  if (filteredLocales.length <= 1) {
    return null;
  }

  return (
    <div className={`relative language-selector-container ${className}`}>
      {/* Language Button */}
      <button
        className="flex w-[40px] h-[40px] rounded-full items-center justify-center hover:bg-white/10 transition-colors"
        title={`Language: ${currentLocaleInfo?.name || currentLocale}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}>
        <Icon name="globe-circle" size={size} color="#FCFCFC" />
      </button>

      {/* Language Dropdown - Works on both desktop and mobile */}
      {showDropdown && (
        <div
          className={`absolute bottom-full right-0 mb-2 bg-black/90 backdrop-blur-sm rounded-lg p-2 min-w-[180px] z-[999999] ${
            isStationMode ? '' : 'sm:left-1/2 sm:transform sm:-translate-x-1/2'
          }`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}>
          <div className="flex flex-col gap-1">
            <div className="text-white/60 text-xs font-medium px-3 py-1 border-b border-white/10 mb-1">
              Select Language
            </div>
            {filteredLocales.map((locale) => (
              <button
                key={locale.code}
                onClick={() => handleLocaleChange(locale.code)}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors ${
                  currentLocale === locale.code
                    ? 'bg-orange-500/20 text-orange-400'
                    : 'hover:bg-white/10 text-white'
                }`}>
                <span className="text-lg">{locale.flag}</span>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{locale.name}</span>
                </div>
              </button>
            ))}
          </div>
          {/* Arrow pointing down */}
          <div
            className={`absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black/90 ${
              isStationMode
                ? ''
                : 'sm:left-1/2 sm:right-auto sm:transform sm:-translate-x-1/2'
            }`}></div>
        </div>
      )}
    </div>
  );
};
